import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Container,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap'
import {
  createNews,
  deleteNews,
  NewsModel,
  NewsState,
  selectIsNewsIdInProgress,
  selectNewsById,
  updateNews,
} from '@store/slices/news.slice'
import { useTheme } from '@config/theme.config'
import './newsEditor.component.scss'
import {
  BsArrowLeft,
  BsArrowRight,
  BsCircleFill,
  BsLock,
  BsMusicNoteList,
  BsPlayFill,
  BsTypeBold,
  BsUnlock,
  BsX,
} from 'react-icons/bs'
import { Controller, useForm } from 'react-hook-form'
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react'
import { withHistory } from 'slate-history'
import { createEditor, Descendant, Editor, Transforms } from 'slate'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import ReactSelect, { components, ControlProps } from 'react-select'
import { reactSelectDarkmode } from '@config/slate.config'
import CreatableSelect from 'react-select/creatable'
import {
  getNewsStateTranslation,
  getStateColor,
} from '../../../tools/newsState.tools'
import {
  areDatesEqual,
  formatDate,
  formatDateNicely,
  formatDateString,
  formatDjangoTimeString,
  formatSeconds,
  formatTimeToMinutes,
  getCharToTimeLength, sortByDatetimeKey,
} from '../../../tools/time.tools'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAllStations,
  selectAllStations,
  selectStationLoading,
} from '@store/slices/station.slice'
import {
  fetchAllAreas,
  selectAllAreas, selectAreaById,
  selectAreaLoading,
} from '@store/slices/area.slice'
import { AudioModel, selectAudioById } from '@store/slices/audio.slice'
import {
  fetchAllVoices,
  selectAllVoices,
  selectVoiceLoading,
} from '@store/slices/voice.slice'
import {
  AudioMessageModel,
  fetchAllAudioMessages,
  selectAllAudioMessages,
  selectAudioNewsLoading,
} from '@store/slices/audioMessage.slice'
import {
  selectAllSections,
  selectSectionLoading,
} from '@store/slices/section.slice'
import { AppDispatch, RootState } from '@store/store'
import CountTimeComponent from '@components/news/newsEditor/countTime.component'
import {
  fetchAudioOutsideStore,
  generateAudioForText,
} from '@store/thunk/audio.thunk'
import AudioPlayButton from '@components/shared/audioPlayButtonComponent'
import { fetchAllSections } from '@store/thunk/section.thunk'
import { getSelectButtonClass } from '../../../tools/style.tools'
import { lockNews, unlockNews } from '@store/thunk/news.thunk'
import { toast } from 'react-toastify'
import {TimeslotModel, updateTimeSlot} from "@store/slices/timeslot.slice";
import {selectBroadcastById} from "@store/slices/broadcast.slice";

export type NewsFormData = {
  text: Descendant[]
  section: string
  stationId: EntityId
  date: string
  areaId: EntityId
  note: string
  title: string
  state: NewsState
  voiceId: EntityId | null
}

const NewsEditorComponent: FC<{
  newsToEditId?: EntityId
  show: boolean
  onHide: (asyncPromise?: Promise<EntityId>) => void,
  slot?: TimeslotModel
}> = ({ newsToEditId, show, onHide, slot = null}) => {
  const { theme } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const {
    register,
    getValues,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
    trigger,
  } = useForm<NewsFormData>()
  const stations = useSelector(selectAllStations)
  const isStationsLoading = useSelector(selectStationLoading)
  const areas = useSelector(selectAllAreas)
  const isAreasLoading = useSelector(selectAreaLoading)
  const sections = useSelector(selectAllSections)
  const isSectionsLoading = useSelector(selectSectionLoading)
  const voices = useSelector(selectAllVoices)
  const isVoicesLoading = useSelector(selectVoiceLoading)
  const audioMessages = useSelector(selectAllAudioMessages)
  const isAudioMessagesLoading = useSelector(selectAudioNewsLoading)
  const [newsId, setNewsId] = useState<EntityId | null>(newsToEditId || null)
  const slotBroadcast = !!slot ? useSelector((state: RootState) =>
      selectBroadcastById(state, slot.broadcastId)
  ) : null
  const slotArea = !!slotBroadcast ? useSelector((state: RootState) =>
      selectAreaById(state, slotBroadcast.areaId)
  ) : null

  const isNewsLoading = useSelector((state: RootState) =>
    newsId ? selectIsNewsIdInProgress(state, newsId) : null
  )
  const newsToEdit = useSelector((state: RootState) =>
    newsId ? selectNewsById(state, newsId) : null
  )
  const newsAudio = useSelector((state: RootState) =>
    newsToEdit ? selectAudioById(state, newsToEdit.audioId) : null
  )
  const [generatedAudio, setGeneratedAudio] = useState<AudioModel | null>(null)
  const [audioGenerating, setAudioGenerating] = useState(false)
  const [manualAudioPlay, setManualAudioPlay] = useState(false)
  const [isNewNewsLoading, setNewNewsLoading] = useState(false)
  const audioLengthRef = useRef()
  const editor = withHistory(withReact(createEditor()))

  useEffect(() => {
    if (audioMessages.length === 0 && !isAudioMessagesLoading)
      dispatch(fetchAllAudioMessages())
    if (voices.length === 0 && !isVoicesLoading) dispatch(fetchAllVoices())
    if (stations.length === 0 && !isStationsLoading)
      dispatch(fetchAllStations())
    if (areas.length === 0 && !isAreasLoading) dispatch(fetchAllAreas())
    if (sections.length === 0 && !isSectionsLoading)
      dispatch(fetchAllSections())
  }, [])

  useEffect(() => {
    setNewsId(newsToEditId || null)
  }, [newsToEditId])

  useEffect(() => {
    reset(getDefaultFormValues())
    setAudioLength(null)
    setGeneratedAudio(null)
  }, [newsToEdit, reset, voices])

  const getDefaultFormValues = () => ({
    title: newsToEdit ? newsToEdit.title : '',
    text:
      newsToEdit && newsToEdit.content
        ? JSON.parse(newsToEdit.content)
        : [
            {
              type: 'paragraph',
              children: [{ text: '' }],
            },
          ],
    section: newsToEdit ? newsToEdit.section : '',
    stationId: newsToEdit ? newsToEdit.stationId : (!!slotArea ? slotArea.stationId : ''),
    date:
      newsToEdit && newsToEdit.date && newsToEdit.date !== ''
        ? formatDateString(newsToEdit.date)
        : (!!slotBroadcast && slotBroadcast.date && slotBroadcast.date !== ''
              ? formatDateString(slotBroadcast.date) : ''),
    areaId: newsToEdit ? newsToEdit.areaId : (!!slotArea ? slotArea.id : ''),
    note: newsToEdit ? newsToEdit.note : '',
    state: newsToEdit ? newsToEdit.state : NewsState.IN_PROGRESS,
    voiceId: voices.length > 0 ? voices[0].id : null,
  })

  const setAudioLength = (newValue: string | null) => {
    if (audioLengthRef.current && newValue)
      //@ts-ignore
      audioLengthRef.current.updateValue(newValue)
  }

  const isMarkActive = (editor: ReactEditor, format: string) => {
    const marks = Editor.marks(editor)
    // @ts-ignore
    return marks ? marks[format] === true : false
  }

  const toggleMark = (editor: ReactEditor, format: string) => {
    const isActive = isMarkActive(editor, format)

    if (isActive) {
      Editor.removeMark(editor, format)
    } else {
      Editor.addMark(editor, format, true)
    }
  }

  const onInsertFile = async (selectedAudioMessageId: EntityId) => {
    const selectedAudioMessage = audioMessages.find(
      (audioNews) => audioNews.id == selectedAudioMessageId
    )
    if (selectedAudioMessage) {
      const audio = await dispatch(
        fetchAudioOutsideStore(selectedAudioMessage.audioId)
      )
        .unwrap()
        .catch((e) => console.error(e))
      if (audio) {
        insertAudio(audio, selectedAudioMessage)
      }
    }
  }

  const insertAudio = (audio: AudioModel, audioMessage: AudioMessageModel) => {
    const audioSrc = audio?.publicPath
    if (audioSrc) {
      const audioNode = {
        type: 'audio',
        src: audioSrc,
        length: audio?.length,
        audioId: audio?.id,
        audioMessageId: audioMessage?.id,
        audioName: audioMessage?.name,
        audioStartWords: audioMessage?.beginningWords,
        audioEndWords: audioMessage?.endingWords,
        children: [{ text: '' }],
      }

      // Insert the audio node at the current selection
      Transforms.insertNodes(editor, audioNode)

      const paragraphNode = { type: 'paragraph', children: [{ text: '' }] }

      // Insert the new paragraph node directly after the current selection
      Transforms.insertNodes(editor, paragraphNode, {
        // @ts-ignore
        at: Editor.after(editor, editor.selection),
        select: true,
      })

      // Move the focus to it
      ReactEditor.focus(editor)
    } else {
      console.log(
        'Failed to find audio forn news: ' + JSON.stringify(audioMessage)
      )
    }
  }

  const onDeleteClick = () => {
    if (newsToEdit && newsToEdit.id && newsToEdit.id !== '')
      dispatch(deleteNews(newsToEdit.id))
        .then(unwrapResult)
        .then(() => {
          toast.success('Zpráva smazána')
          onHide()
        })
        .catch((e) => console.error(e))
  }

  const onUndoClick = () => {
    editor.undo()
  }

  const onRedoClick = () => {
    editor.redo()
  }

  const onLockClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (newsToEditId && newsToEdit) {
      if (!newsToEdit.lockedAt) {
        dispatch(lockNews(newsToEditId)).catch((e) => console.error(e))
      } else {
        dispatch(unlockNews(newsToEditId)).catch((e) => console.error(e))
      }
    }
  }

  const onSubmit = async (e: React.MouseEvent, close = false) => {
    e.preventDefault()
    const isValid = await trigger()
    if (!isValid) return
    const { text, section, stationId, areaId, note, state, date } = getValues()
    const newNews: Partial<NewsModel> = {
      content: JSON.stringify(text),
      ...(areaId ? { areaId } : {}),
      ...(note ? { note } : {}),
      ...(stationId ? { stationId } : {}),
      ...(section ? { section } : {}),
      ...(date ? { date } : {}),
      ...(state ? { state } : {}),
    }
    let newsPromise: Promise<EntityId>
    toast.info('Zpráva se ukládá')
    if (newsToEdit) {
      setNewNewsLoading(true)
      newsPromise = dispatch(
        updateNews({
          id: newsToEdit.id,
          data: newNews,
        })
      )
        .then(unwrapResult)
        .then((editedMessage) => {
          setGeneratedAudio(null)
          toast.success('Zpráva uložena')
          return Promise.resolve(editedMessage.id)
        })
        .catch((error) => console.log(error))
        .finally(() => setNewNewsLoading(false))
    } else {
      setNewNewsLoading(true)
      newsPromise = dispatch(createNews(newNews))
        .then(unwrapResult)
        .then((newMessage) => {
          setNewsId(newMessage.id)
          setGeneratedAudio(null)
          toast.success('Zpráva uložena')
          if (!!slot) {
            dispatch(
              updateTimeSlot({
                id: slot.id,
                data: {
                  newsId: newMessage.id,
                  position: slot.position,
                  broadcastId: slot.broadcastId,
                },
              })
            )
          }
          return Promise.resolve(newMessage.id)
        })
        .catch((error) => console.log(error))
        .finally(() => setNewNewsLoading(false))
    }

    if (close) {
      onHide(newsPromise)
    }
  }

  const resetForm = (e: React.MouseEvent) => {
    e.preventDefault()
    setAudioLength(null)
    reset()
  }

  const calculateApproximateTime = (nodes: any[]): number => {
    let time = 0
    const selectedVoice = voices.find(
      (voice) => voice.id == getValues('voiceId')
    )
    nodes.forEach((node: any) => {
      if (node.type === 'audio') {
        const parts = node.length.split(':')
        const hours = Number(parts[0])
        const minutes = Number(parts[1])
        const seconds = Number(parts[2])
        const totalSeconds = hours * 3600 + minutes * 60 + seconds
        time += node.length ? totalSeconds : 0
      } else if (node.text) {
        const textTime = getCharToTimeLength(
          node.text.length,
          Number(selectedVoice?.readingSpeed) || 0
        )
        time += textTime
      }
      // If the node has children, recursively calculate the time for them as well
      if (node.children && node.children.length > 0) {
        time += calculateApproximateTime(node.children)
      }
    })
    return time
  }

  const onEditorBlur = useCallback((event: React.ChangeEvent) => {
    event.preventDefault()
    const approximation = calculateApproximateTime(getValues('text'))
    const formattedTime = formatTimeToMinutes(approximation)
    setAudioLength(formattedTime)
  }, [])

  const onClose = () => {
    reset()
    onHide()
  }
  const onBoldClick = () => {
    toggleMark(editor, 'bold')
  }

  const playTmpAudio = (tmpAudio: AudioModel) => {
    let audio: HTMLAudioElement | null = document.createElement('audio')

    const cleanup = () => {
      if (audio) {
        audio.removeEventListener('ended', cleanup)
        audio.removeEventListener('canplaythrough', playAudio)
        audio = null
      }
    }

    const playAudio = () => {
      if (!audio) return // safety check
      audio.play().catch((err: Error) => {
        console.log('Failed to play audio:', err)
        cleanup()
      })
    }

    audio.addEventListener('canplaythrough', playAudio, { once: true }) // play audio when can play through
    audio.addEventListener('ended', cleanup, { once: true }) // clean up when audio ended

    audio.src = tmpAudio.publicPath // this starts loading the audio data
  }

  const onSecondMusicClick = () => {
    if (!editor.selection) return

    const fragment = Editor.fragment(editor, editor.selection)
    const selectedText = JSON.stringify(fragment, null, 2)
    dispatch(
      generateAudioForText({
        text: selectedText,
        voice: getValues('voiceId'),
      })
    )
      .then(unwrapResult)
      .then((audio) => playTmpAudio(audio))
      .catch((e) => console.error(e))
  }

  const onGenerateAudio = () => {
    setAudioGenerating(true)
    dispatch(
      generateAudioForText({
        text: JSON.stringify(getValues('text')),
        voice: getValues('voiceId'),
      })
    )
      .then(unwrapResult)
      .then((audio) => {
        setGeneratedAudio(audio)
        toast.success('Audio vygenerováno')
        setManualAudioPlay(true)
      })
      .catch((e) => console.error(e))
      .finally(() => setAudioGenerating(false))
  }

  const Buttons = () => (
    <div className="d-flex gap-1 align-items-center">
      <Button
        title="Zpět"
        style={{ padding: '7px' }}
        variant="secondary"
        className="d-flex align-items-center"
        onClick={onUndoClick}
      >
        <BsArrowLeft></BsArrowLeft>
      </Button>
      <Button
        title="Dopředu"
        style={{ padding: '7px' }}
        variant="secondary"
        className="d-flex align-items-center"
        onClick={onRedoClick}
      >
        <BsArrowRight></BsArrowRight>
      </Button>
      <Button
        title="Označit tluště"
        style={{ padding: '7px' }}
        variant="secondary"
        className="d-flex align-items-center"
        onClick={onBoldClick}
      >
        <BsTypeBold></BsTypeBold>
      </Button>
      <Button
        title="Přehrát úsek"
        style={{ padding: '7px' }}
        variant="secondary"
        className="d-flex align-items-center"
        onClick={onSecondMusicClick}
      >
        <BsPlayFill></BsPlayFill>
      </Button>
      <AudioNewsSelect></AudioNewsSelect>
      {newsToEdit && !newsToEdit?.lockedAt && (
        <Button
          title="Zamknout"
          style={{ padding: '7px' }}
          variant="primary"
          className="d-flex align-items-center"
          onClick={(e) => onLockClick(e)}
        >
          <BsLock></BsLock>
        </Button>
      )}
      {newsToEdit?.lockedAt && (
        <Button
          title="Odemknout"
          style={{ padding: '7px' }}
          variant="warning"
          className="d-flex align-items-center"
          onClick={(e) => onLockClick(e)}
        >
          <BsUnlock></BsUnlock>
        </Button>
      )}
      <AudioPlayButton
        audioId={
          !!generatedAudio ? generatedAudio.id : newsToEdit?.audioId || null
        }
        title={'Přehrát audio'}
        playOnChange={manualAudioPlay}
        setPlayOnChange={setManualAudioPlay}
      ></AudioPlayButton>
      <VoiceSelect></VoiceSelect>
    </div>
  )

  const VoiceSelect = () => {
    const voiceOptions = voices.map((voice) => ({
      value: voice.id,
      label: voice.name,
    }))
    const customStyles = {
      control: () => ({
        borderColor: '#198754',
      }),
    }
    const voiceId = getValues('voiceId')
    return (
      <Controller
        name="voiceId"
        control={control}
        render={({ field }) => (
          <ReactSelect
            defaultValue={
              voiceId
                ? voiceOptions.find((option) => option.value === voiceId)
                : voiceOptions.length > 0
                  ? voiceOptions[0]
                  : null
            }
            placeholder={'Náslech'}
            options={voiceOptions}
            styles={reactSelectDarkmode(theme, customStyles)}
            onChange={(option) => field.onChange(option ? option.value : null)}
            onBlur={() => field.onBlur}
          />
        )}
      />
    )
  }
  const StationSelect = () => {
    const stationOptions = stations.map((station) => ({
      value: station.id,
      label: station.name,
    }))
    const customStyles = errors.stationId && {
      control: () => ({
        borderColor: 'rgba(220, 53, 69)',
      }),
    }
    const { stationId } = getValues()
    return (
      <Controller
        name="stationId"
        control={control}
        rules={{ required: 'Stanice není vybrána' }}
        render={({ field }) => (
          <ReactSelect
            defaultValue={
              stationId
                ? stationOptions.find((option) => option.value === stationId)
                : newsToEdit !== null
                  ? stationOptions.find(
                      (option) => option.value === newsToEdit?.stationId
                    )
                  : null
            }
            placeholder={'Stanice'}
            options={stationOptions}
            styles={{
              ...reactSelectDarkmode(theme, customStyles),
            }}
            onChange={(option) => field.onChange(option ? option.value : null)}
            onBlur={field.onBlur}
          />
        )}
      />
    )
  }

  const AreaSelect = () => {
    const areaId = getValues('areaId')
    const stationId = watch('stationId')
    const areaOptions = areas
      .filter((area) => area.stationId == stationId)
      .map((area) => ({
        value: area.id,
        label: area.api8bcId,
      }))
    const disabledStyle = !stationId && {
      control: () => ({
        backgroundColor: theme === 'dark' ? 'black' : 'lightgray',
      }),
    }
    return (
      <Controller
        name="areaId"
        control={control}
        render={({ field }) => (
          <ReactSelect
            isDisabled={!stationId}
            defaultValue={
              areaId
                ? areaOptions.find((option) => option.value === areaId)
                : newsToEdit !== null
                  ? areaOptions.find(
                      (option) => option.value === newsToEdit?.areaId
                    )
                  : null
            }
            placeholder={'Okruh'}
            options={areaOptions}
            isClearable={true}
            styles={reactSelectDarkmode(theme, disabledStyle)}
            onChange={(option) => field.onChange(option ? option.value : null)}
            onBlur={field.onBlur}
          />
        )}
      />
    )
  }
  const SectionSelect = () => {
    type OptionType = { label: string; value: string }
    const sectionOptions: readonly OptionType[] = sections.map((section) => ({
      value: section,
      label: section,
    }))
    const section = getValues('section')
    return (
      <Controller
        name="section"
        control={control}
        render={({ field: { onChange, onBlur } }) => (
          <CreatableSelect<OptionType, false>
            isClearable
            placeholder={'Sekce'}
            defaultValue={
              section
                ? sectionOptions.find((option) => option.value === section)
                : newsToEdit !== null
                  ? sectionOptions.find(
                      (option) => option.value === newsToEdit?.section
                    )
                  : null
            }
            formatCreateLabel={(inputValue) => `Vytvořit "${inputValue}"`}
            options={sectionOptions}
            onChange={(option) => onChange(option ? option.value : null)}
            onBlur={onBlur}
            styles={reactSelectDarkmode(theme)}
          />
        )}
      />
    )
  }
  const StateSelect = () => (
    <Controller
      name="state"
      control={control}
      defaultValue={newsToEdit?.state}
      render={({ field }) => (
        <Dropdown onSelect={field.onChange}>
          <Dropdown.Toggle
            className={`h-100 d-flex align-items-center gap-2 border-${getStateColor(getValues('state'))}`}
            variant="outline-secondary"
          >
            <BsCircleFill
              className={`text-${getStateColor(getValues('state'))}`}
            ></BsCircleFill>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {Object.keys(NewsState)
              .filter((key) => Number.isInteger(Number(key)))
              .map((key) => (
                <Dropdown.Item
                  key={key}
                  eventKey={key}
                  className={`text-${getStateColor(key)}`}
                >
                  <BsCircleFill></BsCircleFill> {getNewsStateTranslation(key)}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      )}
    />
  )
  const TodayButton = () => {
    let isToday = false
    const date = watch('date')
    if (date) {
      isToday = areDatesEqual(new Date(), [date])
    }
    return (
      <InputGroup className="d-flex flex-nowrap text-nowrap ms-2">
        <Button
          disabled={isToday}
          variant={'outline-secondary'}
          className={getSelectButtonClass(isToday, theme)}
          onClick={() => setValue('date', formatDate(new Date()))}
        >
          Dnes
        </Button>
      </InputGroup>
    )
  }

  const DatePicker = () => (
    <InputGroup>
      <FormControl
        type="date"
        style={{
          minWidth: '180px',
          borderColor: errors.date ? 'rgba(220, 53, 69)' : '',
        }}
        defaultValue={
          newsToEdit && newsToEdit.date && newsToEdit.date !== ''
            ? formatDateString(newsToEdit.date)
            : ''
        }
        {...register('date', { required: 'Datum zprávy není vybráno' })}
      ></FormControl>
    </InputGroup>
  )

  const AudioNewsSelect: FC = () => {
    const [showMenu, setShowMenu] = useState(false)
    const AudioNewsOptions = audioMessages
        .sort((a, b) => sortByDatetimeKey(b, a, 'createdAt'))
        .map((audionNews) => ({
      value: audionNews.id,
      label: `${audionNews.id} - ${audionNews.name.substring(0, 30)}`,
    }))
    const onUploadClick = async (selectedAudioNews: EntityId | null) => {
      setShowMenu(false)
      if (selectedAudioNews) {
        await onInsertFile(selectedAudioNews)
      }
    }
    const customStyles =  {
      menu: () => ({
        minWidth: "500px",
      }),
    }
    return (
      <div className={'d-flex gap-1 align-items-center'}>
        <Button
          title="Vložit audio"
          style={{ padding: '7px', maxHeight: '32px' }}
          variant="secondary"
          className="d-flex align-items-center"
          onClick={() => setShowMenu(!showMenu)}
        >
          <BsMusicNoteList></BsMusicNoteList>
        </Button>
        {showMenu && (
          <ReactSelect
            placeholder={'Vyberte záznam'}
            options={AudioNewsOptions}
            isClearable={true}
            isSearchable={true}
            defaultMenuIsOpen={false}
            styles={reactSelectDarkmode(theme, customStyles)}
            onChange={(option) => onUploadClick(option ? option.value : null)}
          />
        )}
      </div>
    )
  }
  const Selectors = () => (
    <div className="d-flex  gap-1">
      <StationSelect></StationSelect>
      <AreaSelect></AreaSelect>
      <SectionSelect></SectionSelect>
      <TodayButton></TodayButton>
      <DatePicker></DatePicker>
      <StateSelect></StateSelect>
    </div>
  )

  const NewsDetail = () => (
    <div
      className={`d-flex my-1 gap-4 text-${theme === 'dark' ? 'light' : 'dark'}`}
    >
      <span>
        <span className="text-secondary">ID: </span>
        {newsToEdit?.visualId || 'Nová zpráva'}
      </span>
      {generatedAudio && (
        <span>
          <span className="text-secondary">Delká audia: </span>
          {formatSeconds(formatDjangoTimeString(generatedAudio.length))}
        </span>
      )}
      {!generatedAudio && newsAudio && (
        <span>
          <span className="text-secondary">Delká audia: </span>
          {formatSeconds(formatDjangoTimeString(newsAudio.length))}
        </span>
      )}
      <CountTimeComponent ref={audioLengthRef}></CountTimeComponent>
    </div>
  )
  const CloseButtons = () => (
    <div className="d-flex justify-content-between">
      <div className="d-flex  gap-1">
        {newsToEdit && newsToEdit.id && newsToEdit.id !== '' && (
          <Button variant="danger" className="fw-bold" onClick={onDeleteClick}>
            Smazat
          </Button>
        )}
        <Button
          variant="warning"
          className="fw-bold"
          onClick={(e) => resetForm(e)}
        >
          Reset
        </Button>
        <Button variant="secondary" className="fw-bold" onClick={onClose}>
          Zavřít
        </Button>
      </div>

      <div className="d-flex justify-content-end gap-1">
        <Button
          variant="success"
          className="fw-bold"
          onClick={onGenerateAudio}
          disabled={audioGenerating}
        >
          {audioGenerating ? (
            <div style={{ width: '45px' }}>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              <span className="sr-only"></span>
            </div>
          ) : (
            'Generovat jednorázový náslech'
          )}
        </Button>
        <Button
          variant="primary"
          className="fw-bold"
          onClick={(e) => onSubmit(e, false)}
        >
          {isNewsLoading || isNewNewsLoading ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            'Uložit'
          )}
        </Button>
        <Button
          variant="primary"
          className="fw-bold"
          onClick={(e) => onSubmit(e, true)}
        >
          Uložit a zavřít
        </Button>
      </div>
    </div>
  )

  const FormErrors = () => (
    <>
      {errors &&
        Object.keys(errors).map((fieldName, index) => (
          <p className="text-error mb-0" key={index}>
            {/*@ts-ignore*/}
            {errors[fieldName]?.message}
          </p>
        ))}
    </>
  )

  const LockedMessage = () => {
    const lockedAtDate = newsToEdit?.lockedAt
      ? new Date(newsToEdit.lockedAt)
      : null
    const formatTime = lockedAtDate ? formatDateNicely(lockedAtDate) : ''
    return (
      <>
        {newsToEdit && newsToEdit.lockedAt && (
          <p className="text-warning mb-2">
            Tuto zpravu zamknul <strong>{formatTime}</strong> uživatel{' '}
            <strong>{newsToEdit.lockedBy}</strong>
          </p>
        )}
      </>
    )
  }

  const Note = () => (
    <InputGroup>
      <InputGroup.Text>Poznámka</InputGroup.Text>
      <FormControl as="textarea" {...register('note')} />
    </InputGroup>
  )

  const NewsEditor: FC = () => {
    const initialValue = getValues('text') || [
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ]

    const CustomElement = (props: RenderElementProps) => {
      // @ts-ignore
      switch (props.element.type) {
        case 'bold':
          return <strong {...props.attributes}>{props.children}</strong>
        case 'audio':
          /*// @ts-ignore*/
          return (
            <div
              style={{ margin: '0' }}
              className={'my-2 p-2 border border-1 border-success'}
              contentEditable={false}
              {...props.attributes}
            >
              <div>
                <span className="text-secondary py-1 px-1">
                  {/*// @ts-ignore*/}
                  Název audia: {props.element.audioName}
                </span>
                <span className="text-secondary py-1 px-1">
                  Délka audia: {/*// @ts-ignore*/}
                  {formatSeconds(formatDjangoTimeString(props.element.length))}
                </span>
              </div>
              <div>
                <span className="text-secondary py-1 px-1">
                  {/*// @ts-ignore*/}
                  První slova: {props.element.audioStartWords}
                </span>
              </div>
              <div>
                <span className="text-secondary py-1 px-1">
                  {/*// @ts-ignore*/}
                  Poslední slova: {props.element.audioEndWords}
                </span>
              </div>
              <div className="d-flex">
                <audio className={''} controls>
                  {/*// @ts-ignore*/}
                  <source src={props.element.src} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
                <Button
                  variant={'danger'}
                  className={'p-1 rounded-0'}
                  onClick={() => {
                    const path = ReactEditor.findPath(editor, props.element)
                    Transforms.removeNodes(editor, { at: path })
                  }}
                >
                  <BsX style={{ width: '30px', height: '30px' }}></BsX>
                </Button>
              </div>
              <span style={{display: 'none'}}>
                {/* This is a dumb fix for a problem that's part of slate.js by design, therefore an invisible
                span with an empty child text node. If this isn't here it causes the editor to crash sometimes. */}
                {props.children}
              </span>
            </div>
          )
        default:
          return (
            <span className={''} {...props.attributes}>
              {props.children}
            </span>
          )
      }
    }

    const renderLeaf = (props: RenderLeafProps) => {
      // @ts-ignore
      if (props.leaf.bold) {
        return <strong {...props.attributes}>{props.children}</strong>
      }
      return (
        <span className="" {...props.attributes}>
          {props.children}
        </span>
      )
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        editor.insertText('\n') //
      }
    }

    // @ts-ignore
    return (
      <Form.Group controlId="slateField">
        <Controller
          name="text"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Slate
              editor={editor}
              initialValue={initialValue}
              onChange={(value) => field.onChange(value)}
            >
              <Editable
                onKeyDown={handleKeyDown}
                renderElement={CustomElement}
                renderLeaf={renderLeaf}
                className={`bg-input`}
                onBlur={onEditorBlur}
                style={{
                  border:
                    newsToEdit && newsToEdit?.lockedAt
                      ? '2px solid  #f1c40f'
                      : theme === 'light'
                        ? '1px solid #ced4da'
                        : '1px solid #495057',
                  borderRadius: '0.25rem',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  fontSize: '1rem',
                  lineHeight: '1.5',
                  color: theme === 'light' ? '#000' : '#fff',
                  minHeight: '300px',
                }}
              />
            </Slate>
          )}
        />
      </Form.Group>
    )
  }
  return (
    <Modal
      data-bs-theme={theme}
      className="news-editor-modal"
      show={show}
      backdrop="static"
      onHide={onClose}
      style={{
        minWidth: '1000px',
      }}
    >
      <Container
        className={`bg-light-subtle text-${theme === 'light' ? theme : 'light'} p-3`}
      >
        <Form>
          <Row className="my-2 d-flex align-items-center">
            <Selectors></Selectors>
          </Row>
          <Row>
            <Buttons></Buttons>
          </Row>
          <Row className="flex-grow-0 d-flex gap-1">
            <NewsDetail></NewsDetail>
          </Row>
          <Row className="my-2">
            <NewsEditor></NewsEditor>
          </Row>
          <Row>
            <LockedMessage></LockedMessage>
          </Row>
          <Row>
            <Note></Note>
          </Row>
          <Row>
            <FormErrors></FormErrors>
          </Row>
          <Row className="mt-4">
            <CloseButtons></CloseButtons>
          </Row>
        </Form>
      </Container>
    </Modal>
  )
}

export default NewsEditorComponent
